import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { track } from '@vercel/analytics';

// Define types for our context
type Language = 'ar' | 'en';
type Direction = 'rtl' | 'ltr';

interface LanguageContextType {
  language: Language;
  setLanguage: React.Dispatch<React.SetStateAction<Language>>;
  direction: Direction;
  toggleLanguage: () => void;
}

// Create the language context with initial default values
const LanguageContext = createContext<LanguageContextType>({
  language: 'ar',
  setLanguage: () => {},
  direction: 'rtl',
  toggleLanguage: () => {},
});

// Define props type for the provider
interface LanguageProviderProps {
  children: ReactNode;
}

// Create a custom hook to use the language context
export const useLanguage = () => useContext(LanguageContext);

// Create a provider component
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Check for saved language preference in localStorage, default to Arabic
  const [language, setLanguage] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem('preferred-language');
    return (savedLanguage === 'ar' || savedLanguage === 'en') ? savedLanguage : 'ar';
  });

  // Set direction based on language
  const direction: Direction = language === 'ar' ? 'rtl' : 'ltr';

  // Track initial language on mount
  useEffect(() => {
    // Track the initial language preference
    track('language_preference', { language });
  }, [language]);

  // Update localStorage when language changes
  useEffect(() => {
    localStorage.setItem('preferred-language', language);
    document.documentElement.setAttribute('lang', language);
    document.body.setAttribute('dir', direction);
  }, [language, direction]);

  // Function to toggle language
  const toggleLanguage = () => {
    const newLanguage = language === 'ar' ? 'en' : 'ar';
    setLanguage(newLanguage);

    // Track language change event
    track('language_change', {
      from: language,
      to: newLanguage,
      timestamp: new Date().toISOString()
    });
  };

  // Enhanced setLanguage function that also tracks analytics
  const setLanguageWithTracking = (value: React.SetStateAction<Language>) => {
    // Get the new language value
    const newLanguage = typeof value === 'function' ? value(language) : value;

    // Only track if the language is actually changing
    if (newLanguage !== language) {
      track('language_change', {
        from: language,
        to: newLanguage,
        timestamp: new Date().toISOString()
      });
    }

    // Set the language
    setLanguage(value);
  };

  // Value to be provided to consumers
  const value: LanguageContextType = {
    language,
    setLanguage: setLanguageWithTracking,
    direction,
    toggleLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};